import { Broadcast, Buildings, DotsThree } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useDmMqttSources } from "@/api/districts/district-queries";
import { useActivateSource } from "@/api/reseller/mutations";
import type { MqttSource } from "@/api/reseller/types";
import {
	type Column,
	MeterTable,
	usePagination,
} from "@/components/meters/meter-table";
import { CopyableCell } from "@/components/shared/copyable-cell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyCell } from "@/components/ui/empty-cell";
import { useDistrict } from "@/contexts/district-context";
import { useDebounce } from "@/hooks/use-debounce";
import { AssignSourceToDistrictDialog } from "./assign-source-to-district-dialog";
import { BulkAssignSourceToDistrictDialog } from "./bulk-assign-source-to-district-dialog";
import { DmSendReadingDialog } from "./dm-send-reading-dialog";
import { DmSourceSelectionBar } from "./dm-source-selection-bar";

const SEND_READING_BUFFER_MS = 60_000;

function nextMinuteAfterBuffer() {
	const d = new Date(Date.now() + SEND_READING_BUFFER_MS);
	d.setSeconds(0, 0);
	d.setMinutes(d.getMinutes() + 1);
	return d;
}

export function DmMqttTable() {
	const { t, i18n } = useTranslation();
	const { districtId } = useDistrict();
	const { page, setPage } = usePagination();
	const [pageSize, setPageSize] = useState(20);
	const [search, setSearch] = useState("");
	const debouncedSearch = useDebounce(search);
	const [ordering, setOrdering] = useState("");
	const [assignSn, setAssignSn] = useState<string | null>(null);
	const [assignDistrictId, setAssignDistrictId] = useState<number | null>(null);
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [showBulkAssign, setShowBulkAssign] = useState(false);
	const [sendReadingSn, setSendReadingSn] = useState<string | null>(null);
	const [sendReadingType, setSendReadingType] = useState<
		"mqtt" | "advant" | null
	>(null);
	const [sendReadingAt, setSendReadingAt] = useState<Date | null>(null);
	const [sendReadingMinDate, setSendReadingMinDate] = useState<Date | null>(
		null,
	);
	const [prevDistrictId, setPrevDistrictId] = useState(districtId);

	if (districtId !== prevDistrictId) {
		setPrevDistrictId(districtId);
		setPage(1);
		setSelected(new Set());
	}

	const { data, isLoading } = useDmMqttSources({
		page,
		page_size: pageSize,
		search: debouncedSearch || undefined,
		ordering: ordering || undefined,
		district_id: districtId ?? undefined,
	});

	const activateMutation = useActivateSource();

	const rows = data?.results ?? [];
	const selectedSns = Array.from(selected);
	const isAllSelected =
		rows.length > 0 && rows.every((r) => selected.has(r.sn));
	const isIndeterminate =
		!isAllSelected && rows.some((r) => selected.has(r.sn));

	function toggleKey(sn: string) {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(sn)) next.delete(sn);
			else next.add(sn);
			return next;
		});
	}

	function toggleAll() {
		setSelected(isAllSelected ? new Set() : new Set(rows.map((r) => r.sn)));
	}

	function openAssign(row: MqttSource) {
		setAssignSn(row.sn);
		setAssignDistrictId(row.district);
	}

	function closeAssign() {
		setAssignSn(null);
		setAssignDistrictId(null);
	}

	function openSendReading(row: MqttSource) {
		setSendReadingSn(row.sn);
		setSendReadingType(row.type);
		if (row.type === "advant") {
			setSendReadingAt(null);
			setSendReadingMinDate(null);
			return;
		}
		const next = nextMinuteAfterBuffer();
		setSendReadingAt(next);
		setSendReadingMinDate(next);
	}

	function closeSendReading() {
		setSendReadingSn(null);
		setSendReadingType(null);
		setSendReadingAt(null);
		setSendReadingMinDate(null);
	}

	const isAdvant = sendReadingType === "advant";
	const isPastDate =
		!isAdvant && sendReadingAt
			? sendReadingAt.getTime() < (sendReadingMinDate?.getTime() ?? 0)
			: false;
	const canSubmitSendReading = isAdvant ? true : !!sendReadingAt && !isPastDate;

	function confirmSendReading() {
		if (!sendReadingSn) return;
		const minStartMs = Date.now() + SEND_READING_BUFFER_MS;
		let startTime: string;
		if (isAdvant) {
			startTime = new Date(minStartMs).toISOString();
		} else if (sendReadingAt) {
			const ms = Math.max(sendReadingAt.getTime(), minStartMs);
			startTime = new Date(ms).toISOString();
		} else {
			return;
		}
		activateMutation.mutate(
			{
				sn: sendReadingSn,
				start_time: startTime,
			},
			{
				onSuccess: (response) => {
					const detail = (response as { detail?: string } | undefined)?.detail;
					toast.success(detail ?? t("mqttSources.activateSourceSuccess"));
					closeSendReading();
				},
				onError: (error) => {
					const errorData = (
						error as {
							response?: {
								data?: {
									detail?: string;
									error?: {
										message?: string;
										details?: Record<string, string[] | string>;
									};
								};
							};
						}
					)?.response?.data;
					const fieldMessage = Object.values(errorData?.error?.details ?? {})
						.flat()
						.find((v): v is string => typeof v === "string" && v.length > 0);
					toast.error(
						fieldMessage ??
							errorData?.error?.message ??
							errorData?.detail ??
							t("mqttSources.activateSourceError"),
					);
				},
			},
		);
	}

	const columns: Column<MqttSource>[] = [
		{
			key: "sn",
			label: t("mqttSources.colSerialNumber"),
			sortField: "sn",
			render: (row) => (
				<CopyableCell value={row.sn} label={t("mqttSources.colSerialNumber")} />
			),
		},
		{
			key: "district",
			label: t("mqttSources.colDistrict"),
			render: (row) =>
				row.district_name ??
				(row.district != null ? String(row.district) : null) ?? <EmptyCell />,
		},
		{
			key: "meters_count",
			label: t("mqttSources.colMeters"),
			render: (row) => row.meters_count,
		},
		{
			key: "next_reading_date",
			label: t("mqttSources.colNextReading"),
			render: (row) =>
				row.next_reading_date ? (
					new Date(row.next_reading_date).toLocaleDateString(i18n.language)
				) : (
					<EmptyCell />
				),
		},
		{
			key: "_details",
			label: t("mqttSources.colLastReading"),
			render: (row) =>
				row.last_reading ? (
					<div className="flex items-center gap-2">
						<Badge variant={row.last_reading.completed ? "success" : "info"}>
							{row.last_reading.completed
								? t("mqttSourceDetail.completed")
								: t("mqttSourceDetail.inProgress")}
						</Badge>
						<span className="text-xs text-muted-foreground">
							{new Date(row.last_reading.reading_date).toLocaleDateString(
								i18n.language,
							)}
						</span>
					</div>
				) : (
					<EmptyCell />
				),
		},
		{
			key: "_actions",
			label: "",
			render: (row) => (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button
							variant="ghost"
							size="icon"
							aria-label={t("common.actions")}
						>
							<DotsThree className="size-4" weight="bold" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem onClick={() => openAssign(row)}>
							<Buildings className="size-4" />
							{t("mqttSources.assignToDistrict")}
						</DropdownMenuItem>
						<DropdownMenuItem
							disabled={activateMutation.isPending}
							onClick={() => openSendReading(row)}
						>
							<Broadcast className="size-4" />
							{t(
								row.type === "advant"
									? "mqttSources.sendReadingNow"
									: "mqttSources.sendReading",
							)}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			),
		},
	];

	const selectionBar = (
		<DmSourceSelectionBar
			count={selectedSns.length}
			onClear={() => setSelected(new Set())}
			onAssign={() => setShowBulkAssign(true)}
		/>
	);

	return (
		<>
			<MeterTable
				data={data}
				columns={columns}
				loading={isLoading}
				page={page}
				pageSize={pageSize}
				onPageChange={setPage}
				onPageSizeChange={(size) => {
					setPageSize(size);
					setPage(1);
				}}
				search={search}
				onSearchChange={(val) => {
					setSearch(val);
					setPage(1);
				}}
				ordering={ordering}
				onOrderingChange={(val) => {
					setOrdering(val);
					setPage(1);
				}}
				rowKeyField="sn"
				selectedKeys={selectedSns}
				onSelectKey={toggleKey}
				onSelectAll={toggleAll}
				isAllSelected={isAllSelected}
				isIndeterminate={isIndeterminate}
				selectionBar={selectionBar}
			/>
			{assignSn ? (
				<AssignSourceToDistrictDialog
					open={!!assignSn}
					onOpenChange={(o) => !o && closeAssign()}
					sn={assignSn}
					currentDistrictId={assignDistrictId}
				/>
			) : null}
			<BulkAssignSourceToDistrictDialog
				open={showBulkAssign}
				onOpenChange={setShowBulkAssign}
				selectedSns={selectedSns}
				onSuccess={() => setSelected(new Set())}
			/>
			<DmSendReadingDialog
				sn={sendReadingSn}
				type={sendReadingType}
				at={sendReadingAt}
				minDate={sendReadingMinDate}
				isPending={activateMutation.isPending}
				isPastDate={isPastDate}
				canSubmit={canSubmitSendReading}
				onAtChange={setSendReadingAt}
				onOpenChange={(o) => !o && closeSendReading()}
				onConfirm={confirmSendReading}
			/>
		</>
	);
}
